import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { fetchProjectOverview, fetchProjectWorkItems, fetchProjectLoggedOther, updateAssignment, deleteAssignment, fetchPlanningLookups, deleteTimeEntry, fetchTimeEntry, API_BASE_URL } from '../api';
import { formatMinutesToHM, formatDateDMY, formatRangeDMY } from '../ui/format';
import TimeEntryDetailsModal from '../ui/TimeEntryDetailsModal.jsx';
import SegmentedControl from '../components/SegmentedControl.jsx';
import Calendar2 from '../components/manager/Calendar2.jsx';
import SharedAddWorkModal from '../components/manager/SharedAddWorkModal.jsx';
import toast from 'react-hot-toast';
import Card from '../ui/Card.jsx';
import Table from '../ui/Table.jsx';
import { assignmentColumnClasses, summaryGridCols, progressBarWidth } from '../ui/tableLayouts';
import Chip from '../ui/Chip.jsx';
import Badge from '../ui/Badge.jsx';
import { getActiveTheme, isRedFocusThemeName, resolveThemeName } from '../ui/tokens';
import { shadows } from '../ui/tokens';

function toYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function startOfDay(d) {
  const dt = (d instanceof Date) ? new Date(d) : new Date(d);
  if (isNaN(dt.getTime())) return new Date(NaN);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function getWeekRange(rangeKey, today = new Date()) {
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfWeek = (d) => {
    const tmp = new Date(d);
    const dow = tmp.getDay();
    const offset = (dow === 0 ? -6 : 1 - dow); // Monday
    tmp.setDate(tmp.getDate() + offset);
    return new Date(tmp.getFullYear(), tmp.getMonth(), tmp.getDate());
  };
  const endOfWeek = (d) => { const s = startOfWeek(d); const e = new Date(s); e.setDate(s.getDate() + 6); return e; };
  const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
  const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
  let fromDate, toDate;
  switch (rangeKey) {
    case 'last_week': { const b = new Date(base); b.setDate(b.getDate() - 7); fromDate = startOfWeek(b); toDate = endOfWeek(b); break; }
    case 'next_week': { const b = new Date(base); b.setDate(b.getDate() + 7); fromDate = startOfWeek(b); toDate = endOfWeek(b); break; }
    case 'last_month': { const b = new Date(base.getFullYear(), base.getMonth() - 1, 1); fromDate = startOfMonth(b); toDate = endOfMonth(b); break; }
    case 'this_month': { fromDate = startOfMonth(base); toDate = endOfMonth(base); break; }
    case 'this_week':
    default: { fromDate = startOfWeek(base); toDate = endOfWeek(base); break; }
  }
  return { from: toYMD(fromDate), to: toYMD(toDate), label: formatRangeDMY(fromDate, toDate) };
}

function normalizeName(s) {
  return String(s || '').trim().toLowerCase();
}

function isSeededDemoAccountName(displayName) {
  const name = normalizeName(displayName);
  if (!name) return false;
  return name === 'admin test'
    || name === 'manager test'
    || name === 'admin and manager test'
    || name === 'employee test';
}

function isAdminAccountName(displayName) {
  const name = normalizeName(displayName);
  if (!name) return false;
  return name === 'admin' || name === 'admin user';
}

export default function ProjectPage() {
  const isRedFocus = isRedFocusThemeName(resolveThemeName());
  const warmTheme = isRedFocus ? getActiveTheme() : null;
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const projectId = useMemo(() => {
    // Route param is string; keep parsing strict and predictable.
    const parsed = Number.parseInt(String(id || ''), 10);
    return Number.isFinite(parsed) ? parsed : NaN;
  }, [id]);
  const hasValidProjectId = Number.isFinite(projectId) && projectId > 0;
  const [range, setRange] = useState('total');
  // Data range drives fetching + date window; UI range can be "total" without triggering new fetch.
  const [dataRange, setDataRange] = useState('this_month');
  const dateWindow = useMemo(() => getWeekRange(dataRange, new Date()), [dataRange]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadErrors, setLoadErrors] = useState({ overview: '', workItems: '', loggedOther: '' });
  const [overview, setOverview] = useState(null);
  const [items, setItems] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loggedOther, setLoggedOther] = useState([]);
  const [timeEntryDetailsOpen, setTimeEntryDetailsOpen] = useState(false);
  const [timeEntryDetailsId, setTimeEntryDetailsId] = useState(null);
  const [showTopOnly, setShowTopOnly] = useState(false);
  const [meRole, setMeRole] = useState(null);
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('overview'); // overview | calendar
  const [addWorkOpen, setAddWorkOpen] = useState(false);
  const [addWorkAssignmentId, setAddWorkAssignmentId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState(null);
  const [lookups, setLookups] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteLoggedOtherOpen, setDeleteLoggedOtherOpen] = useState(false);
  const [deleteLoggedOtherRow, setDeleteLoggedOtherRow] = useState(null);
  const [deleteLoggedOtherSaving, setDeleteLoggedOtherSaving] = useState(false);

  const autoLogsStorageKey = useMemo(() => {
    const uid = me?.id ? String(me.id) : 'unknown';
    return `project_autoLogs_collapsed::${uid}`;
  }, [me?.id]);

  const [autoLogsCollapsed, setAutoLogsCollapsed] = useState(true);

  const canManageTasks = ['manager', 'admin'].includes(String(meRole || '').toLowerCase());
  const isMeAdminLike = String(meRole || '').toLowerCase() === 'admin' || String(me?.display_name || '').trim().toLowerCase() === 'admin';
  const isEmployee = String(meRole || '').toLowerCase() === 'employee';

  const isTotal = String(range) === 'total';

  const rangeFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    const v = params.get('range') || params.get('period') || '';
    return String(v || '').trim();
  }, [location.search]);

  useEffect(() => {
    if (!rangeFromQuery) return;
    const allowed = new Set(['last_week', 'this_week', 'next_week', 'last_month', 'this_month', 'total']);
    if (!allowed.has(rangeFromQuery)) return;
    setRange(rangeFromQuery);
    if (rangeFromQuery !== 'total') setDataRange(rangeFromQuery);
  }, [rangeFromQuery]);

  const coerceToYmd = (v) => {
    if (!v) return '';
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    const d = (v instanceof Date) ? v : new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    return toYMD(d);
  };

  const totalWindow = useMemo(() => {
    const start = coerceToYmd(overview?.project?.start_date);
    const end = coerceToYmd(overview?.project?.end_date);
    if (!start || !end) return null;
    return start <= end ? { from: start, to: end } : { from: end, to: start };
  }, [overview?.project?.start_date, overview?.project?.end_date]);

  const effectiveWindow = useMemo(() => {
    if (isTotal && totalWindow) return totalWindow;
    return { from: dateWindow.from, to: dateWindow.to };
  }, [isTotal, totalWindow, dateWindow.from, dateWindow.to]);

  // Decorate assignments
  const decoratedAssignments = useMemo(() => {
    const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const todayObj = new Date();
    const td = startOfDay(todayObj);
    return (assignments || []).map(a => {
      const dueDateObj = a.due_date ? new Date(a.due_date) : null;
      const dd = dueDateObj ? startOfDay(dueDateObj) : null;
      let daysUntilDue = null;
      if (dd) daysUntilDue = Math.round((dd.getTime() - td.getTime()) / (24*60*60*1000));
      const statusLower = (a.status || '').toLowerCase();
      const isDone = statusLower === 'gotovo';
      const isOverdue = dd ? (dd.getTime() < td.getTime()) && !isDone : false;
      const isDueSoon = dd ? (daysUntilDue != null && daysUntilDue >= 0 && daysUntilDue <= 3) && !isDone : false;
      const isTop = !!a.top_priority;
      return { ...a, _dueDateObj: dueDateObj, _daysUntilDue: daysUntilDue, _isDone: isDone, _isOverdue: isOverdue, _isDueSoon: isDueSoon, _isTop: isTop };
    });
  }, [assignments]);

  // Groups
  const groups = useMemo(() => {
    const byKey = new Map();
    const addRow = (key, name, row) => {
      if (!byKey.has(key)) byKey.set(key, { key, name, rows: [] });
      byKey.get(key).rows.push(row);
    };
    decoratedAssignments
      .filter(a => showTopOnly ? a._isTop : true)
      .forEach(a => {
        const jid = a.job?.id ?? a.job_id ?? null;
        const jname = a.job?.name ?? a.job_name ?? '—';
        const key = jid != null ? `id:${jid}` : `name:${jname}`;
        addRow(key, jname, a);
      });
    const sortRows = (arr) => arr.slice().sort((a,b) => {
      const ad = a._dueDateObj ? a._dueDateObj.getTime() : Number.POSITIVE_INFINITY;
      const bd = b._dueDateObj ? b._dueDateObj.getTime() : Number.POSITIVE_INFINITY;
      return ad - bd;
    });
    const groupsArr = Array.from(byKey.values()).map(g => {
      const rows = sortRows(g.rows);
      const subMap = new Map();
      const addSubRow = (skey, label, row) => {
        if (!subMap.has(skey)) subMap.set(skey, { key: skey, label, rows: [] });
        subMap.get(skey).rows.push(row);
      };
      rows.forEach(a => {
        const tid = (a.task?.id != null) ? a.task.id : (a.task_id != null ? a.task_id : null);
        const sKey = tid != null ? `task:${tid}` : 'task:null';
        const sLabel = tid != null ? (a.task?.name || a.task_name || 'Task') : '(No task)';
        addSubRow(sKey, sLabel, a);
      });
      let subgroups = Array.from(subMap.values()).map(sg => {
        const sRows = sortRows(sg.rows);
        const plannedTotal = sRows.reduce((sum, r) => sum + (r.planned_minutes || 0), 0);
        const loggedATotal = sRows.reduce((sum, r) => sum + (r.logged_assigned_minutes || 0), 0);
        const loggedOTotal = sRows.reduce((sum, r) => sum + (r.logged_other_minutes || 0), 0);
        const loggedTotal = loggedATotal + loggedOTotal;
        const progressPct = plannedTotal > 0 ? Math.min(100, Math.floor((loggedTotal / plannedTotal) * 100)) : 0;
        const earliestStartTs = sRows.reduce((minTs, r) => {
          if (!r.start_date) return minTs;
          const dt = new Date(r.start_date);
          const ts = isNaN(dt.getTime()) ? Number.POSITIVE_INFINITY : startOfDay(dt).getTime();
          return Math.min(minTs, ts);
        }, Number.POSITIVE_INFINITY);
        const earliestDueTs = sRows.reduce((minTs, r) => {
          const ts = r._dueDateObj ? r._dueDateObj.getTime() : Number.POSITIVE_INFINITY;
          return Math.min(minTs, ts);
        }, Number.POSITIVE_INFINITY);
        const dueDates = new Set();
        const startDates = new Set();
        const ownersSet = new Map();
        let hasTop = false;
        const statusCounts = new Map();
        sRows.forEach(r => {
          if (r.due_date) dueDates.add(r.due_date);
          if (r.start_date) startDates.add(r.start_date);
          (Array.isArray(r.owners) ? r.owners : []).forEach(o => {
            ownersSet.set(o.user_id, o.display_name || `#${o.user_id}`);
          });
          if (r.top_priority) hasTop = true;
          const st = (r.status || '');
          statusCounts.set(st, (statusCounts.get(st) || 0) + 1);
        });
        const owners = Array.from(ownersSet.entries()).map(([id, name]) => ({ user_id: id, display_name: name })).sort((a,b)=> (a.display_name||'').localeCompare(b.display_name||''));
        const dueCount = dueDates.size;
        const startCount = startDates.size;
        let status = 'najavi';
        let max = -1;
        statusCounts.forEach((cnt, st) => { if (cnt > max) { max = cnt; status = st; } });
        return { ...sg, rows: sRows, plannedTotal, loggedATotal, loggedOTotal, progressPct, earliestStartTs, earliestDueTs, owners, dueCount, startCount, hasTop, status };
      });
      subgroups.sort((a,b) => a.earliestDueTs - b.earliestDueTs);
      const plannedTotal = rows.reduce((sum, r) => sum + (r.planned_minutes || 0), 0);
      const loggedATotal = rows.reduce((sum, r) => sum + (r.logged_assigned_minutes || 0), 0);
      const loggedOTotal = rows.reduce((sum, r) => sum + (r.logged_other_minutes || 0), 0);
      const loggedTotal = loggedATotal + loggedOTotal;
      const progressPct = plannedTotal > 0 ? Math.min(100, Math.floor((loggedTotal / plannedTotal) * 100)) : 0;
      return { ...g, rows, plannedTotal, loggedATotal, loggedOTotal, progressPct, subgroups };
    });
    groupsArr.sort((a,b) => a.name.localeCompare(b.name));
    return groupsArr;
  }, [decoratedAssignments, showTopOnly]);

  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedSubgroups, setExpandedSubgroups] = useState({});
  useEffect(() => {
    const next = {};
    groups.forEach(g => { next[g.key] = true; });
    setExpandedGroups(next);
    const subNext = {};
    groups.forEach(g => {
      (g.subgroups || []).forEach(sg => { subNext[`${g.key}|${sg.key}`] = false; });
    });
    setExpandedSubgroups(subNext);
  }, [groups]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError('');
      setLoadErrors({ overview: '', workItems: '', loggedOther: '' });
      try {
        // 1) Overview is always fetched for dataRange (server supports week/month ranges, not total).
        let ovValue = null;
        try {
          ovValue = await fetchProjectOverview(projectId, isTotal ? null : dataRange);
        } catch (err) {
          if (!cancelled) {
            setOverview(null);
            setLoadErrors(prev => ({ ...prev, overview: err?.message || 'Failed to load overview' }));
            setError(err?.message || 'Failed to load project');
          }
        }

        if (!cancelled) setOverview(ovValue || null);

        // 2) Work items + logged-other use either the UI dateWindow or (when Total) the project's full date span.
        const totalFrom = coerceToYmd(ovValue?.project?.start_date);
        const totalTo = coerceToYmd(ovValue?.project?.end_date);
        const totalOk = !!(totalFrom && totalTo);
        const windowFrom = (isTotal && totalOk) ? (totalFrom <= totalTo ? totalFrom : totalTo) : effectiveWindow.from;
        const windowTo = (isTotal && totalOk) ? (totalFrom <= totalTo ? totalTo : totalFrom) : effectiveWindow.to;

        const [wiRes, loRes] = await Promise.allSettled([
          fetchProjectWorkItems(projectId, windowFrom, windowTo),
          fetchProjectLoggedOther(projectId, windowFrom, windowTo),
        ]);

        if (!cancelled) {
          if (wiRes.status === 'fulfilled') {
            const wi = wiRes.value;
            setItems(Array.isArray(wi?.items) ? wi.items : []);
            setAssignments(Array.isArray(wi?.assignments) ? wi.assignments : []);
          } else {
            setItems([]);
            setAssignments([]);
            setLoadErrors(prev => ({ ...prev, workItems: wiRes.reason?.message || 'Failed to load tasks' }));
            if (!error) setError(wiRes.reason?.message || 'Failed to load tasks');
          }

          if (loRes.status === 'fulfilled') {
            const lo = loRes.value;
            const rows = Array.isArray(lo)
              ? lo
              : (Array.isArray(lo?.logged_other) ? lo.logged_other : []);
            setLoggedOther(rows);
          } else {
            setLoggedOther([]);
            setLoadErrors(prev => ({ ...prev, loggedOther: loRes.reason?.message || 'Failed to load other logged time' }));
            if (!error) setError(loRes.reason?.message || 'Failed to load logged time');
          }
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load project');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (hasValidProjectId) load();
    return () => { cancelled = true; };
  }, [hasValidProjectId, projectId, dataRange, effectiveWindow.from, effectiveWindow.to, isTotal]);

  const loggedOtherByDay = useMemo(() => {
    const map = new Map();
    (Array.isArray(loggedOther) ? loggedOther : []).forEach(r => {
      const day = r?.date || r?.entry_date;
      if (!day) return;
      const arr = map.get(day) || [];
      arr.push(r);
      map.set(day, arr);
    });
    // sort each day by user_name then date
    for (const [day, arr] of map.entries()) {
      arr.sort((a,b) => {
        const au = String(a?.user_name || '');
        const bu = String(b?.user_name || '');
        if (au !== bu) return au.localeCompare(bu);
        const ad = String(a?.date || a?.entry_date || '');
        const bd = String(b?.date || b?.entry_date || '');
        return ad.localeCompare(bd);
      });
    }
    return Array.from(map.entries()).sort((a,b) => String(a[0]).localeCompare(String(b[0])));
  }, [loggedOther]);

  const loggedOtherTotals = useMemo(() => {
    const rows = Array.isArray(loggedOther) ? loggedOther : [];
    const total = rows.reduce((sum, r) => sum + (Number(r?.minutes || 0) || 0), 0);
    return { total };
  }, [loggedOther]);

  const groupLoggedByDay = (rows) => {
    const map = new Map();
    (Array.isArray(rows) ? rows : []).forEach(r => {
      const day = r?.date || r?.entry_date;
      if (!day) return;
      const arr = map.get(day) || [];
      arr.push(r);
      map.set(day, arr);
    });
    for (const [day, arr] of map.entries()) {
      arr.sort((a,b) => {
        const au = String(a?.user_name || '');
        const bu = String(b?.user_name || '');
        if (au !== bu) return au.localeCompare(bu);
        const ad = String(a?.created_at || a?.createdAt || '');
        const bd = String(b?.created_at || b?.createdAt || '');
        return ad.localeCompare(bd);
      });
    }
    return Array.from(map.entries()).sort((a,b) => String(a[0]).localeCompare(String(b[0])));
  };

  const loggedOtherSystemRows = useMemo(() => {
    const rows = Array.isArray(loggedOther) ? loggedOther : [];
    return rows.filter(r => (r?.source === 'AUTO_FINALIZE') || !!r?.is_system);
  }, [loggedOther]);

  const loggedOtherManualRows = useMemo(() => {
    const rows = Array.isArray(loggedOther) ? loggedOther : [];
    return rows.filter(r => !((r?.source === 'AUTO_FINALIZE') || !!r?.is_system));
  }, [loggedOther]);

  const loggedOtherSystemByDay = useMemo(() => groupLoggedByDay(loggedOtherSystemRows), [loggedOtherSystemRows]);
  const loggedOtherManualByDay = useMemo(() => groupLoggedByDay(loggedOtherManualRows), [loggedOtherManualRows]);

  const loggedOtherSystemTotals = useMemo(() => {
    const total = (Array.isArray(loggedOtherSystemRows) ? loggedOtherSystemRows : []).reduce((sum, r) => sum + (Number(r?.minutes || 0) || 0), 0);
    return { total };
  }, [loggedOtherSystemRows]);

  const loggedOtherManualTotals = useMemo(() => {
    const total = (Array.isArray(loggedOtherManualRows) ? loggedOtherManualRows : []).reduce((sum, r) => sum + (Number(r?.minutes || 0) || 0), 0);
    return { total };
  }, [loggedOtherManualRows]);

  const computedKpiForWindow = useMemo(() => {
    const rows = Array.isArray(assignments) ? assignments : [];
    const planned = rows.reduce((sum, a) => sum + (Number(a?.planned_minutes || 0) || 0), 0);
    const loggedAssigned = rows.reduce((sum, a) => sum + (Number(a?.logged_assigned_minutes || 0) || 0), 0);
    const loggedOtherTotal = Number(loggedOtherTotals.total || 0) || 0;
    const loggedTotal = loggedAssigned + loggedOtherTotal;
    return {
      planned_minutes_total: planned,
      logged_assigned_minutes_total: loggedAssigned,
      logged_other_minutes_total: loggedOtherTotal,
      logged_minutes_total: loggedTotal,
      overbooked_days_count: 0,
    };
  }, [assignments, loggedOtherTotals.total]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') {
        setDeleteLoggedOtherOpen(false);
        setDeleteLoggedOtherRow(null);
      }
    }
    if (deleteLoggedOtherOpen) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deleteLoggedOtherOpen]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const j = await res.json();
        if (!cancelled) {
          setMeRole(j?.user?.role || null);
          setMe(j?.user || null);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(autoLogsStorageKey);
      if (raw == null) return;
      const v = String(raw).trim().toLowerCase();
      setAutoLogsCollapsed(v === '1' || v === 'true' || v === 'yes');
    } catch {}
  }, [autoLogsStorageKey]);

  useEffect(() => {
    try {
      localStorage.setItem(autoLogsStorageKey, autoLogsCollapsed ? '1' : '0');
    } catch {}
  }, [autoLogsStorageKey, autoLogsCollapsed]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const l = await fetchPlanningLookups();
        if (!cancelled) setLookups(l);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  const proj = overview?.project || null;
  const kpi = overview?.kpi || null;

  const overviewTeamForSummary = useMemo(() => {
    const canSeeSeeded = normalizeName(me?.email) === 'admin@example.com';
    const rows = (Array.isArray(overview?.team) ? overview.team : [])
      .filter(u => !isAdminAccountName(u?.display_name))
      .filter(u => (canSeeSeeded ? true : !isSeededDemoAccountName(u?.display_name)));
    const pinnedFirstNames = ['vinko', 'sanela', 'andrea'];
    const isPinned = (name) => {
      const full = String(name || '').trim().toLowerCase();
      const first = (full.split(/\s+/)[0] || full).trim();
      return pinnedFirstNames.includes(first);
    };
    const hasEffectiveAssignmentSignal = (u) => {
      const v =
        u?.effective_assignments_count ??
        u?.visibility_assignments_count ??
        u?.planned_assignments_count ??
        u?.assignments_count ??
        0;
      return Number(v || 0) > 0;
    };

    return rows.filter(u => {
      const planned = Number(u?.planned_minutes_total || 0) || 0;
      const logged = Number(u?.logged_minutes_total || 0) || 0;
      if (planned > 0 || logged > 0) return true;
      if (isPinned(u?.display_name) && hasEffectiveAssignmentSignal(u)) return true;
      return false;
    });
  }, [overview?.team, me?.email]);
  const displayKpi = isTotal ? computedKpiForWindow : kpi;

  if (!hasValidProjectId) {
    console.warn('ProjectPage: missing/invalid project id param:', id);
    return (
      <div className="p-4">
        <div className="text-xl font-semibold">Project</div>
        <div className="mt-2 text-sm text-gray-700 dark:text-gray-300">
          No project selected (missing or invalid project id in the URL).
        </div>
        <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
          Expected route: <span className="font-mono">/projects/:projectId</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-2xl font-bold">{proj?.name || `Project #${projectId}`}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">{proj?.client_name || '—'}</div>
          {proj && (
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Date: {proj.start_date ? formatDateDMY(proj.start_date) : '—'} – {proj.end_date ? formatDateDMY(proj.end_date) : '—'}</div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="text-sm">{isTotal ? 'Total' : dateWindow.label}</div>
          <SegmentedControl
            ariaLabel="Project range"
            options={[
              { key: 'last_week', label: 'Last week' },
              { key: 'this_week', label: 'This week' },
              { key: 'next_week', label: 'Next week' },
              { key: 'last_month', label: 'Last month' },
              { key: 'this_month', label: 'This month' },
              { key: 'total', label: 'Total' },
            ]}
            value={range}
            onChange={(next) => {
              setRange(next);
              if (String(next) !== 'total') setDataRange(next);
            }}
            size="sm"
          />
        </div>
      </div>

      <div className="mb-3">
        <SegmentedControl ariaLabel="Project tabs" ariaRole="tablist" options={[{key:'overview',label:'Overview'},{key:'calendar',label:'Calendar'}]} value={tab} onChange={setTab} />
      </div>

      {loading && <div className="text-sm text-gray-600 dark:text-gray-400">Loading…</div>}
      {error && <div className="text-sm text-red-600">{error}</div>}

      {!loading && tab === 'overview' && (
        <div className="space-y-4">
          <div className="flex items-center justify-end">
            <button
              className={`ui-btn px-3 py-1 rounded text-white ${isRedFocus ? 'hover:brightness-95' : 'bg-blue-600 hover:bg-blue-700'}`}
              style={isRedFocus ? { background: warmTheme?.primary || '#e11d48' } : undefined}
              onClick={() => { setAddWorkAssignmentId(null); setAddWorkOpen(true); }}
            >Assign work</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className={resolveThemeName() === 'auroraExecutiveLight' ? 'p-4' : 'rounded border border-gray-300 dark:border-gray-700 p-4 bg-white dark:bg-gray-800'}
              style={resolveThemeName() === 'auroraExecutiveLight'
                ? {
                  borderRadius: getActiveTheme()?.cardRadius || 12,
                  border: `1px solid ${getActiveTheme()?.border || '#E5E9F0'}`,
                  background: getActiveTheme()?.surface || '#FFFFFF',
                  boxShadow: getActiveTheme()?.cardShadow || '0 4px 20px rgba(0,0,0,0.04)',
                }
                : undefined}
            >
              <div className="text-lg font-semibold mb-2">KPIs</div>
              <div className="text-sm space-y-1">
                <div>Planned: {formatMinutesToHM(displayKpi?.planned_minutes_total || 0)}</div>
                <div>Logged (planned): {formatMinutesToHM(displayKpi?.logged_assigned_minutes_total || 0)}</div>
                <div>Logged (other): {formatMinutesToHM(displayKpi?.logged_other_minutes_total || 0)}</div>
                <div>Logged total: {formatMinutesToHM(displayKpi?.logged_minutes_total || 0)}</div>
                <div>Overbooked days: {displayKpi?.overbooked_days_count != null ? displayKpi.overbooked_days_count : 0}</div>
              </div>
            </div>
            <div
              className={resolveThemeName() === 'auroraExecutiveLight' ? 'p-4' : 'rounded border border-gray-300 dark:border-gray-700 p-4 bg-white dark:bg-gray-800'}
              style={resolveThemeName() === 'auroraExecutiveLight'
                ? {
                  borderRadius: getActiveTheme()?.cardRadius || 12,
                  border: `1px solid ${getActiveTheme()?.border || '#E5E9F0'}`,
                  background: getActiveTheme()?.surface || '#FFFFFF',
                  boxShadow: getActiveTheme()?.cardShadow || '0 4px 20px rgba(0,0,0,0.04)',
                }
                : undefined}
            >
              <div className="text-lg font-semibold mb-2">Team</div>
              <div className="space-y-1 text-sm">
                {overviewTeamForSummary.map(u => (
                  <div key={u.user_id} className="flex items-center justify-between">
                    <div>{u.display_name || 'User'}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Planned {formatMinutesToHM(u.planned_minutes_total || 0)} • Logged {formatMinutesToHM(u.logged_minutes_total || 0)}</div>
                  </div>
                ))}
                {overviewTeamForSummary.length === 0 && <div className="text-sm text-gray-600">No team data</div>}
              </div>
            </div>
          </div>

          {/* Embedded Tasks section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-400">Tasks — {isTotal ? 'Total' : dateWindow.label}</div>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={showTopOnly} onChange={e=>setShowTopOnly(e.target.checked)} />
                <span>Show Top priority only</span>
              </label>
            </div>

            {loadErrors.loggedOther && (
              <div className="text-xs text-red-600">{loadErrors.loggedOther}</div>
            )}

            {(loggedOtherSystemByDay.length > 0 || loggedOtherManualByDay.length > 0) && (
              <div className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 space-y-3">
                {loggedOtherSystemByDay.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        className="flex items-center gap-2 text-sm font-medium hover:opacity-80"
                        onClick={() => setAutoLogsCollapsed(v => !v)}
                        aria-expanded={!autoLogsCollapsed}
                      >
                        <span className="text-gray-600 dark:text-gray-300" aria-hidden>{autoLogsCollapsed ? '▸' : '▾'}</span>
                        <span>Auto-finalized logs</span>
                      </button>
                      <div className="text-xs text-gray-600 dark:text-gray-400">Total {formatMinutesToHM(loggedOtherSystemTotals.total)}</div>
                    </div>

                    {!autoLogsCollapsed && (
                      <div className="mt-2 space-y-2">
                        {loggedOtherSystemByDay.map(([day, rows]) => (
                          <div key={`sys-${day}`} className="border border-gray-200 dark:border-gray-800 rounded p-2">
                            <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">{formatDateDMY(day)}</div>
                            <div className="flex flex-wrap gap-2">
                              {rows.map(r => {
                                const who = r?.user_name || '—';
                                const mins = Number(r?.minutes || 0) || 0;
                                const category = r?.category_name || r?.job?.name || 'System';
                                const label = r?.notes ? `${category} • ${r.notes}` : category;
                                return (
                                  <button
                                    key={r.id}
                                    type="button"
                                    className="inline-flex items-center gap-2 px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-[11px] text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
                                    title={r?.notes || ''}
                                    onClick={() => {
                                      setTimeEntryDetailsId(r?.id || null);
                                      setTimeEntryDetailsOpen(true);
                                    }}
                                  >
                                    <span className="font-medium">{who}</span>
                                    <span className="text-gray-600 dark:text-gray-400">{label || 'Logged time'}</span>
                                    <span className="tabular-nums">{formatMinutesToHM(mins)}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {loggedOtherManualByDay.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium">Other logs</div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">Total {formatMinutesToHM(loggedOtherManualTotals.total)}</div>
                    </div>
                    <div className="space-y-2">
                      {loggedOtherManualByDay.map(([day, rows]) => (
                        <div key={`manual-${day}`} className="border border-gray-200 dark:border-gray-800 rounded p-2">
                          <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">{formatDateDMY(day)}</div>
                          <div className="flex flex-wrap gap-2">
                            {rows.map(r => {
                              const who = r?.user_name || '—';
                              const mins = Number(r?.minutes || 0) || 0;
                              const category = r?.category_name || r?.job?.name || 'Other';
                              const label = r?.notes ? `${category} • ${r.notes}` : category;
                              const canDelete = !!(r?.id && !r?.locked && !r?.is_system && (isMeAdminLike || (me?.id && r?.user_id && String(me.id) === String(r.user_id))));
                              return (
                                <span
                                  key={r.id}
                                  className="inline-flex items-center gap-2 px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-[11px] text-gray-800 dark:text-gray-200"
                                  title={r?.notes || ''}
                                >
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-2 hover:opacity-80"
                                    onClick={() => {
                                      setTimeEntryDetailsId(r?.id || null);
                                      setTimeEntryDetailsOpen(true);
                                    }}
                                  >
                                    <span className="font-medium">{who}</span>
                                    <span className="text-gray-600 dark:text-gray-400">{label || 'Logged time'}</span>
                                    <span className="tabular-nums">{formatMinutesToHM(mins)}</span>
                                  </button>
                                  {canDelete && (
                                    <button
                                      type="button"
                                      className="ml-1 text-[11px] px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
                                      title="Delete"
                                      onClick={async (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setDeleteLoggedOtherRow({ ...r, _label: label, _who: who, _mins: mins, _day: day });
                                        setDeleteLoggedOtherOpen(true);
                                      }}
                                    >Delete</button>
                                  )}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {loadErrors.workItems && (
              <div className="text-xs text-red-600">{loadErrors.workItems}</div>
            )}

            {groups.length === 0 ? (
              loggedOtherByDay.length === 0
                ? <div className="text-sm text-gray-600">{isTotal ? 'No work' : 'No work in range'}</div>
                : null
            ) : (
              groups.map(group => {
                const expanded = !!expandedGroups[group.key];
                const taskCount = (group.subgroups || []).length;
                const jobChevron = expanded ? '▾' : '▸';
                return (
                  <div
                    key={group.key}
                    className={
                      (resolveThemeName() === 'auroraExecutiveLight' || resolveThemeName() === 'redLightPremium')
                        ? 'rounded-lg p-3 border border-gray-200 dark:border-gray-800'
                        : 'rounded-lg p-3 bg-gradient-to-r from-indigo-50 via-pink-50 to-amber-50 dark:from-slate-800 dark:via-fuchsia-900/30 dark:to-amber-900/30 border border-gray-200 dark:border-gray-800'
                    }
                    style={{
                      boxShadow: (resolveThemeName() === 'auroraExecutiveLight' || resolveThemeName() === 'redLightPremium')
                        ? (getActiveTheme()?.cardShadow || '0 4px 20px rgba(0,0,0,0.04)')
                        : shadows.sm,
                      ...((resolveThemeName() === 'auroraExecutiveLight' || resolveThemeName() === 'redLightPremium')
                        ? {
                          background: getActiveTheme()?.surface || '#FFFFFF',
                          borderColor: getActiveTheme()?.border || '#E5E9F0',
                          borderRadius: getActiveTheme()?.cardRadius || 12,
                        }
                        : null),
                      ...(isRedFocus
                        ? { background: `linear-gradient(90deg, ${warmTheme?.surface2 || '#fff1f2'} 0%, #ffffff 100%)` }
                        : null),
                    }}
                  >
                    {/* Job Header Card */}
                    <button
                      type="button"
                      className="w-full flex items-start justify-between gap-3 text-left"
                      onClick={() => setExpandedGroups(prev => ({ ...prev, [group.key]: !expanded }))}
                      aria-expanded={expanded}
                      aria-label={expanded ? 'Collapse job' : 'Expand job'}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-lg font-semibold truncate">{group.name}</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">{taskCount} task{taskCount===1?'':'s'}</div>
                      </div>
                      <div className="hidden sm:flex items-center gap-4">
                        <div className="text-xs">Planned {formatMinutesToHM(group.plannedTotal)}</div>
                        <div className="text-xs">Logged: {formatMinutesToHM(group.loggedATotal)}</div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-2 py-0.5 rounded bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-gray-100 whitespace-nowrap">{group.progressPct}%</span>
                          <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
                            <div className="bg-gray-500 h-2" style={{ width: `${group.progressPct}%` }} />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-end w-6 text-gray-600 dark:text-gray-300" aria-hidden>
                        <span className="text-lg leading-none">{jobChevron}</span>
                      </div>
                    </button>

                    {/* Task Rows */}
                    {expanded && (
                      <div className="mt-3 space-y-2">
                        {(group.subgroups || []).map(sg => {
                          const sKey = `${group.key}|${sg.key}`;
                          const sExpanded = !!expandedSubgroups[sKey];
                          const taskChevron = sExpanded ? '▾' : '▸';
                          const owners = sg.owners || [];
                          const ownersPreview = owners.slice(0,2);
                          const ownersMore = Math.max(0, owners.length - ownersPreview.length);
                          const startLabel = (() => {
                            if (sg.earliestStartTs == null || sg.earliestStartTs === Number.POSITIVE_INFINITY) return '—';
                            const d = new Date(sg.earliestStartTs);
                            return formatDateDMY(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
                          })();
                          const dueLabel = (() => {
                            if (sg.earliestDueTs === Number.POSITIVE_INFINITY) return '—';
                            const d = new Date(sg.earliestDueTs);
                            return formatDateDMY(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
                          })();
                          return (
                            <div key={sg.key} className="rounded bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-2">
                              <div
                                className="flex items-start justify-between gap-2 cursor-pointer select-none"
                                role="button"
                                tabIndex={0}
                                aria-expanded={sExpanded}
                                onClick={() => setExpandedSubgroups(prev => ({ ...prev, [sKey]: !sExpanded }))}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setExpandedSubgroups(prev => ({ ...prev, [sKey]: !sExpanded }));
                                  }
                                }}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium truncate">
                                    {sg.label}
                                  </div>
                                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-gray-700 dark:text-gray-300">
                                    <div className="flex items-center gap-1">
                                      {ownersPreview.length === 0 ? (
                                        <span className="text-gray-500">No owners</span>
                                      ) : ownersPreview.map(o => (
                                        <Chip key={o.user_id} label={o.display_name} />
                                      ))}
                                      {ownersMore > 0 && <span className="text-gray-500">+{ownersMore}</span>}
                                    </div>
                                      {startLabel !== '—' ? (
                                        <div className="text-gray-600 dark:text-gray-400">
                                          Start {startLabel}{sg.startCount>1?` +${sg.startCount-1} dates`:''} • Due {dueLabel}{sg.dueCount>1?` +${sg.dueCount-1} dates`:''}
                                        </div>
                                      ) : (
                                        <div className="text-gray-600 dark:text-gray-400">Due {dueLabel}{sg.dueCount>1?` +${sg.dueCount-1} dates`:''}</div>
                                      )}
                                  </div>
                                </div>
                                <div className="hidden sm:grid grid-cols-3 gap-3 items-center mr-3 text-xs">
                                  <div>Planned {formatMinutesToHM(sg.plannedTotal)}</div>
                                  <div>Logged: {formatMinutesToHM(sg.loggedATotal)}</div>
                                  <div className="flex items-center gap-2">
                                    <span className="px-2 py-0.5 rounded bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-gray-100 whitespace-nowrap">{sg.progressPct}%</span>
                                    <div className="w-20 h-2 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
                                      <div className={`bg-gray-500 h-2`} style={{ width: `${sg.progressPct}%` }} />
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {sg.hasTop && <Badge label="!" variant="warning" />}
                                  {canManageTasks && (
                                    <button
                                      className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                                      onClick={async (e)=>{
                                        e.stopPropagation();
                                        try {
                                          const target = !sg.hasTop;
                                          for (const r of (sg.rows||[])) {
                                            await updateAssignment(r.assignment_id, { top_priority: target });
                                          }
                                          setAssignments(prev => prev.map(x => {
                                            const inTask = (sg.rows||[]).some(r => r.assignment_id === x.assignment_id);
                                            return inTask ? { ...x, top_priority: target } : x;
                                          }));
                                        } catch {}
                                      }}
                                    >{sg.hasTop ? 'Unset' : 'Set'} top</button>
                                  )}
                                  <span className="text-lg text-gray-600 dark:text-gray-300 w-6 text-right" aria-hidden>{taskChevron}</span>
                                </div>
                              </div>

                              {sExpanded && (
                                <div className="mt-2">
                                  <Table>
                                    <thead>
                                      <tr className="text-left text-[11px] text-gray-600 dark:text-gray-400">
                                        <th className="py-1 pr-3">Owner(s)</th>
                                        <th className="py-1 pr-3">Accepted</th>
                                        <th className="py-1 pr-3">Progress</th>
                                        <th className="py-1 pr-3">Start</th>
                                        <th className="py-1 pr-3">Due</th>
                                        <th className="py-1 pr-3">Planned</th>
                                        <th className="py-1 pr-3">Logged</th>
                                        <th className="py-1 pr-3">Notes</th>
                                        {canManageTasks && <th className="py-1 pr-3">Actions</th>}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {sg.rows.slice().sort((a,b)=>{
                                        const ad = a._dueDateObj ? a._dueDateObj.getTime() : Number.POSITIVE_INFINITY;
                                        const bd = b._dueDateObj ? b._dueDateObj.getTime() : Number.POSITIVE_INFINITY;
                                        if (ad !== bd) return ad - bd;
                                        const ao = (Array.isArray(a.owners) && a.owners[0]?.display_name) ? a.owners[0].display_name : '';
                                        const bo = (Array.isArray(b.owners) && b.owners[0]?.display_name) ? b.owners[0].display_name : '';
                                        return (ao||'').localeCompare(bo||'');
                                      }).map(a => {
                                        const planned = a.planned_minutes || 0;
                                        const loggedA = a.logged_assigned_minutes || 0;
                                        const start = a.start_date ? formatDateDMY(a.start_date) : '—';
                                        const due = a.due_date ? formatDateDMY(a.due_date) : '—';
                                        const owners = Array.isArray(a.owners) ? a.owners : [];
                                        const note = (a.notes || '').trim();
                                        const noteLabel = note ? `Assignment note: ${note}` : '';
                                        return (
                                          <tr
                                            key={a.assignment_id}
                                            className={`border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 ${canManageTasks ? 'cursor-pointer' : ''}`}
                                            onClick={() => {
                                              if (!canManageTasks) return;
                                              setAddWorkAssignmentId(a.assignment_id);
                                              setAddWorkOpen(true);
                                            }}
                                          >
                                            <td className="py-1 pr-3">
                                              <div className="flex flex-wrap gap-1">
                                                {owners.length === 0 ? (
                                                  <span className="text-xs text-gray-500">—</span>
                                                ) : owners.map(o => (
                                                  <Chip key={o.user_id} label={o.display_name || `#${o.user_id}`} />
                                                ))}
                                              </div>
                                            </td>
                                            <td className="py-1 pr-3">
                                              {owners.length === 0 ? (
                                                <span className="text-xs text-gray-400">—</span>
                                              ) : (
                                                <div className="space-y-0.5">
                                                  {owners.map(o => (
                                                    <div key={o.user_id} className="text-[11px] text-gray-700 dark:text-gray-300">
                                                      {o.accepted_at ? 'Yes' : 'No'}
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                            </td>
                                            <td className="py-1 pr-3">
                                              {owners.length === 0 ? (
                                                <span className="text-xs text-gray-400">—</span>
                                              ) : (
                                                <div className="space-y-0.5">
                                                  {owners.map(o => (
                                                    <div key={o.user_id} className="text-[11px] text-gray-700 dark:text-gray-300">
                                                      {Number(o.progress || 0)}
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                            </td>
                                            <td className="py-1 pr-3">{start}</td>
                                            <td className="py-1 pr-3">{due}</td>
                                            <td className="py-1 pr-3">{formatMinutesToHM(planned)}</td>
                                            <td className="py-1 pr-3">{formatMinutesToHM(loggedA)}</td>
                                            <td className="py-1 pr-3">
                                              {note ? (
                                                <span className="text-[11px] text-gray-700 dark:text-gray-300" title={noteLabel}>
                                                  {note.length > 42 ? `${note.slice(0, 42)}…` : note}
                                                </span>
                                              ) : (
                                                <span className="text-[11px] text-gray-400">—</span>
                                              )}
                                            </td>
                                            {canManageTasks && (
                                              <td className="py-1 pr-3">
                                                <div className="flex items-center gap-2">
                                                  <button
                                                    type="button"
                                                    className="text-[11px] px-2 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    onClick={async (e) => {
                                                      e.stopPropagation();
                                                      setAddWorkAssignmentId(a.assignment_id);
                                                      setAddWorkOpen(true);
                                                    }}
                                                  >Edit</button>

                                                  <button
                                                    type="button"
                                                    className="text-[11px] px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-200 dark:hover:bg-red-900/30"
                                                    onClick={async (e) => {
                                                      e.stopPropagation();
                                                      const ok = globalThis.confirm('Delete this task? This will remove planned work and delete the assignment.');
                                                      if (!ok) return;
                                                      try {
                                                        await deleteAssignment(a.assignment_id);
                                                        toast.success('Task deleted');
                                                        const ov = await fetchProjectOverview(projectId, isTotal ? null : dataRange);
                                                        setOverview(ov || null);
                                                          const fromYmd = (isTotal && ov?.project?.start_date && ov?.project?.end_date)
                                                            ? coerceToYmd(ov.project.start_date)
                                                            : effectiveWindow.from;
                                                          const toYmd = (isTotal && ov?.project?.start_date && ov?.project?.end_date)
                                                            ? coerceToYmd(ov.project.end_date)
                                                            : effectiveWindow.to;
                                                          const wi = await fetchProjectWorkItems(projectId, fromYmd, toYmd);
                                                        setItems(Array.isArray(wi?.items) ? wi.items : []);
                                                        setAssignments(Array.isArray(wi?.assignments) ? wi.assignments : []);
                                                        if (editId === a.assignment_id) { setEditId(null); setEdit(null); }
                                                      } catch (err) {
                                                        toast.error(err?.message || 'Failed to delete');
                                                      }
                                                    }}
                                                  >Delete</button>
                                                </div>
                                              </td>
                                            )}
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </Table>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {addWorkOpen && proj && (
        <SharedAddWorkModal
          project={proj}
          assignmentId={addWorkAssignmentId}
          rangeKey={dataRange}
          overview={overview}
          onClose={()=> { setAddWorkOpen(false); setAddWorkAssignmentId(null); }}
          onCreated={async ()=>{
            try {
              const ov = await fetchProjectOverview(projectId, isTotal ? null : dataRange);
              setOverview(ov || null);
              const fromYmd = (isTotal && ov?.project?.start_date && ov?.project?.end_date)
                ? coerceToYmd(ov.project.start_date)
                : effectiveWindow.from;
              const toYmd = (isTotal && ov?.project?.start_date && ov?.project?.end_date)
                ? coerceToYmd(ov.project.end_date)
                : effectiveWindow.to;
              const wi = await fetchProjectWorkItems(projectId, fromYmd, toYmd);
              setItems(Array.isArray(wi?.items) ? wi.items : []);
              setAssignments(Array.isArray(wi?.assignments) ? wi.assignments : []);
            } catch {}
            setAddWorkOpen(false);
            setAddWorkAssignmentId(null);
          }}
          onSaved={async ()=>{
            try {
              const ov = await fetchProjectOverview(projectId, isTotal ? null : dataRange);
              setOverview(ov || null);
              const fromYmd = (isTotal && ov?.project?.start_date && ov?.project?.end_date)
                ? coerceToYmd(ov.project.start_date)
                : effectiveWindow.from;
              const toYmd = (isTotal && ov?.project?.start_date && ov?.project?.end_date)
                ? coerceToYmd(ov.project.end_date)
                : effectiveWindow.to;
              const wi = await fetchProjectWorkItems(projectId, fromYmd, toYmd);
              setItems(Array.isArray(wi?.items) ? wi.items : []);
              setAssignments(Array.isArray(wi?.assignments) ? wi.assignments : []);
            } catch {}
            setAddWorkOpen(false);
            setAddWorkAssignmentId(null);
          }}
        />
      )}

      

      {!loading && tab === 'calendar' && (
        <div className="space-y-3">
          <Calendar2
            today={new Date()}
            storageNamespace={`calendar_project_${projectId}`}
            fixedProjectId={projectId}
            hideProjectFilter
            enableAssignWorkCta
            onWorkCreated={async () => {
              try {
                const ov = await fetchProjectOverview(projectId, isTotal ? null : dataRange);
                setOverview(ov || null);
                const fromYmd = (isTotal && ov?.project?.start_date && ov?.project?.end_date)
                  ? coerceToYmd(ov.project.start_date)
                  : effectiveWindow.from;
                const toYmd = (isTotal && ov?.project?.start_date && ov?.project?.end_date)
                  ? coerceToYmd(ov.project.end_date)
                  : effectiveWindow.to;
                const wi = await fetchProjectWorkItems(projectId, fromYmd, toYmd);
                setItems(Array.isArray(wi?.items) ? wi.items : []);
                setAssignments(Array.isArray(wi?.assignments) ? wi.assignments : []);
              } catch {}
            }}
          />
        </div>
      )}

      {/* Removed Other tab */}

      {/* Assignment Drawer */}
      {canManageTasks && editId != null && edit && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-end z-50" onClick={() => { if (!saving) { setEditId(null); setEdit(null); } }}>
          <div className="w-full max-w-md h-full bg-white dark:bg-gray-800 border-l border-gray-300 dark:border-gray-700 shadow-xl p-4 overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
              <div className="text-lg font-semibold">Edit Assignment</div>
              <button className="text-sm px-2 py-1 rounded border" onClick={() => { if (!saving) { setEditId(null); setEdit(null); } }}>Close</button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs mb-1">Job</label>
                <select className="w-full border rounded p-2 text-sm" value={edit.job_id || ''} onChange={(e)=>{
                  const jid = e.target.value ? Number(e.target.value) : null;
                  setEdit(prev => ({...prev, job_id: jid, task_id: null}));
                }}>
                  <option value="">Select job</option>
                  {(lookups?.jobs||[]).map(j => (<option key={j.id} value={j.id}>{j.name}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-xs mb-1">Task</label>
                <select className="w-full border rounded p-2 text-sm" value={edit.task_id || ''} onChange={(e)=>{
                  const tid = e.target.value ? Number(e.target.value) : null;
                  setEdit(prev => ({...prev, task_id: tid}));
                }} disabled={!edit.job_id}>
                  <option value="">Select task</option>
                  {edit.job_id && (lookups?.tasksByJob?.[edit.job_id]||[]).map(t => (<option key={t.id} value={t.id}>{t.name}</option>))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs mb-1">Planned Hours</label>
                  <input type="number" min="0" step="0.25" className="w-full border rounded p-2 text-sm" value={edit.planned_hours} onChange={(e)=>setEdit(prev=>({...prev, planned_hours: Number(e.target.value||0)}))} />
                </div>
                <div>
                  <label className="block text-xs mb-1">Top Priority</label>
                  <input type="checkbox" className="mr-2" checked={!!edit.top_priority} onChange={(e)=>setEdit(prev=>({...prev, top_priority: e.target.checked}))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs mb-1">Start Date (optional)</label>
                  <input type="date" className="w-full border rounded p-2 text-sm" value={edit.start_date || ''} onChange={(e)=>{
                    const v = e.target.value || '';
                    setEdit(prev=>{
                      const nextStart = v ? v : null;
                      const next = { ...prev, start_date: nextStart };
                      if (nextStart && next.due_date && String(next.due_date) < String(nextStart)) {
                        next.due_date = nextStart;
                      }
                      return next;
                    });
                  }} />
                </div>
                <div>
                  <label className="block text-xs mb-1">Due Date</label>
                  <input type="date" className="w-full border rounded p-2 text-sm" value={edit.due_date || ''} onChange={(e)=>{
                    const v = e.target.value || '';
                    setEdit(prev=>{
                      const nextDue = v;
                      const next = { ...prev, due_date: nextDue };
                      if (next.start_date && nextDue && String(nextDue) < String(next.start_date)) {
                        next.due_date = next.start_date;
                      }
                      return next;
                    });
                  }} />
                </div>
              </div>
              <div>
                <label className="block text-xs mb-1">Assignees</label>
                <div className="flex flex-wrap gap-2">
                  {(lookups?.employees||[]).map(u => {
                    const checked = edit.assignees.includes(u.id);
                    return (
                      <label key={u.id} className="inline-flex items-center gap-1 text-xs border rounded px-2 py-1">
                        <input type="checkbox" checked={checked} onChange={(e)=>{
                          setEdit(prev => {
                            const set = new Set(prev.assignees);
                            if (e.target.checked) set.add(u.id); else set.delete(u.id);
                            return { ...prev, assignees: Array.from(set) };
                          });
                        }} />
                        <span>{u.display_name || `#${u.id}`}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Progress determines lifecycle.
                </div>
              </div>
              <div>
                <label className="block text-xs mb-1">Notes</label>
                <textarea className="w-full border rounded p-2 text-sm" rows={3} value={edit.notes || ''} onChange={(e)=>setEdit(prev=>({...prev, notes: e.target.value}))} />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button className="text-sm px-3 py-1 rounded border" onClick={()=>{ setEditId(null); setEdit(null); }}>Cancel</button>
                <button
                  className="text-sm px-3 py-1 rounded bg-indigo-600 text-white"
                  disabled={saving}
                  onClick={async ()=>{
                    try {
                      setSaving(true);
                      if (edit.start_date && edit.due_date && String(edit.start_date) > String(edit.due_date)) {
                        toast.error('Start date must be before or equal to due date');
                        return;
                      }
                      const payload = {
                        job_id: edit.job_id,
                        task_id: edit.task_id,
                        planned_hours: edit.planned_hours,
                        start_date: edit.start_date,
                        due_date: edit.due_date,
                        assignee_user_ids: edit.assignees,
                        top_priority: !!edit.top_priority,
                        notes: edit.notes || '',
                      };
                      await updateAssignment(editId, payload);
                      toast.success('Assignment updated');
                      // Refresh overview + work items
                      const ov = await fetchProjectOverview(projectId, isTotal ? null : dataRange);
                      setOverview(ov || null);
                      const wi = await fetchProjectWorkItems(projectId, effectiveWindow.from, effectiveWindow.to);
                      setItems(Array.isArray(wi?.items) ? wi.items : []);
                      setAssignments(Array.isArray(wi?.assignments) ? wi.assignments : []);
                      setEditId(null); setEdit(null);
                    } catch (err) {
                      toast.error(err?.message || 'Failed to save');
                    } finally {
                      setSaving(false);
                    }
                  }}
                >Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {timeEntryDetailsOpen && timeEntryDetailsId && (
        <TimeEntryDetailsModal
          open={timeEntryDetailsOpen}
          timeEntryId={timeEntryDetailsId}
          fetcher={fetchTimeEntry}
          viewer={me}
          onChanged={async () => {
            try {
              const windowFrom = effectiveWindow.from;
              const windowTo = effectiveWindow.to;
              const lo = await fetchProjectLoggedOther(projectId, windowFrom, windowTo);
              const rows = Array.isArray(lo)
                ? lo
                : (Array.isArray(lo?.logged_other) ? lo.logged_other : []);
              setLoggedOther(rows);
            } catch {}
          }}
          onClose={() => { setTimeEntryDetailsOpen(false); setTimeEntryDetailsId(null); }}
        />
      )}

      {deleteLoggedOtherOpen && deleteLoggedOtherRow && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-[6px]"
            onClick={() => {
              if (deleteLoggedOtherSaving) return;
              setDeleteLoggedOtherOpen(false);
              setDeleteLoggedOtherRow(null);
            }}
          />
          <div className="absolute left-1/2 top-1/2 w-[92vw] max-w-[520px] -translate-x-1/2 -translate-y-1/2 rounded bg-white dark:bg-gray-900 shadow-xl border border-gray-200 dark:border-gray-800">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">Delete logged entry?</div>
                <div className="text-[11px] text-gray-600 dark:text-gray-400 truncate">
                  {formatDateDMY(deleteLoggedOtherRow?.date || deleteLoggedOtherRow?._day)}
                  {deleteLoggedOtherRow?._who ? ` • ${deleteLoggedOtherRow._who}` : ''}
                  {deleteLoggedOtherRow?._mins != null ? ` • ${formatMinutesToHM(deleteLoggedOtherRow._mins)}` : ''}
                </div>
              </div>
              <button
                onClick={() => {
                  if (deleteLoggedOtherSaving) return;
                  setDeleteLoggedOtherOpen(false);
                  setDeleteLoggedOtherRow(null);
                }}
                className="rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
              >✕</button>
            </div>

            <div className="p-4 space-y-3">
              <div className="text-sm text-gray-800 dark:text-gray-200">
                {deleteLoggedOtherRow?._label || (deleteLoggedOtherRow?.category_name || 'Other')}
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="text-sm px-3 py-1 rounded border"
                  disabled={deleteLoggedOtherSaving}
                  onClick={() => {
                    setDeleteLoggedOtherOpen(false);
                    setDeleteLoggedOtherRow(null);
                  }}
                >Cancel</button>
                <button
                  type="button"
                  className="text-sm px-3 py-1 rounded bg-red-600 text-white disabled:opacity-60"
                  disabled={deleteLoggedOtherSaving}
                  onClick={async () => {
                    try {
                      setDeleteLoggedOtherSaving(true);
                      await deleteTimeEntry(deleteLoggedOtherRow.id);
                      toast.success('Entry deleted');

                      const windowFrom = effectiveWindow.from;
                      const windowTo = effectiveWindow.to;
                      const [ov, wi, lo] = await Promise.all([
                        fetchProjectOverview(projectId, isTotal ? null : dataRange),
                        fetchProjectWorkItems(projectId, windowFrom, windowTo),
                        fetchProjectLoggedOther(projectId, windowFrom, windowTo),
                      ]);
                      setOverview(ov || null);
                      setItems(Array.isArray(wi?.items) ? wi.items : []);
                      setAssignments(Array.isArray(wi?.assignments) ? wi.assignments : []);
                      const rows = Array.isArray(lo)
                        ? lo
                        : (Array.isArray(lo?.logged_other) ? lo.logged_other : []);
                      setLoggedOther(rows);

                      setDeleteLoggedOtherOpen(false);
                      setDeleteLoggedOtherRow(null);
                    } catch (err) {
                      toast.error(err?.message || 'Failed to delete');
                    } finally {
                      setDeleteLoggedOtherSaving(false);
                    }
                  }}
                >Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
